import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Store as WineryIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import BackButton from '../../components/common/BackButton';

// Importar el servicio real de Firebase
import { firebaseWineriesService } from '../../services/firebaseService';

const AdminWineries = () => {
  const [open, setOpen] = useState(false);
  const [editingWinery, setEditingWinery] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    region: '',
    founded: '',
    description: '',
    website: ''
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, winery: null });

  const queryClient = useQueryClient();

  // Query para obtener bodegas usando Firebase
  const { data: wineries = [], isLoading, error } = useQuery({
    queryKey: ['firebaseWineries'],
    queryFn: async () => {
      try {
        console.log('🏭 AdminWineries: Consultando bodegas desde Firebase...');
        const response = await firebaseWineriesService.getAll();
        console.log('✅ Bodegas obtenidas de Firebase:', response.length);
        return response;
      } catch (error) {
        console.error('❌ Error obteniendo bodegas de Firebase:', error);
        throw error;
      }
    }
  });

  // Mutation para crear bodega usando Firebase
  const createMutation = useMutation({
    mutationFn: (formData) => firebaseWineriesService.create(formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseWineries']);
      setOpen(false);
      resetForm();
      toast.success('Bodega creada exitosamente');
    },
    onError: (error) => {
      console.error('❌ Error al crear bodega:', error);
      toast.error('Error al crear la bodega');
    }
  });

  // Mutation para actualizar bodega usando Firebase
  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) => firebaseWineriesService.update(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseWineries']);
      setOpen(false);
      resetForm();
      toast.success('Bodega actualizada exitosamente');
    },
    onError: (error) => {
      console.error('❌ Error al actualizar bodega:', error);
      toast.error('Error al actualizar la bodega');
    }
  });

  // Mutation para eliminar bodega usando Firebase
  const deleteMutation = useMutation({
    mutationFn: firebaseWineriesService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseWineries']);
      setDeleteDialog({ open: false, winery: null });
      toast.success('Bodega eliminada exitosamente');
    },
    onError: (error) => {
      console.error('❌ Error al eliminar bodega:', error);
      toast.error('Error al eliminar la bodega');
      setDeleteDialog({ open: false, winery: null });
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      region: '',
      founded: '',
      description: '',
      website: ''
    });
    setEditingWinery(null);
  };

  const handleOpenDialog = (winery = null) => {
    if (winery) {
      setEditingWinery(winery);
      setFormData({
        name: winery.name,
        region: winery.region,
        founded: winery.founded?.toString() || '',
        description: winery.description,
        website: winery.website || ''
      });
    } else {
      resetForm();
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    if (!formData.region.trim()) {
      toast.error('La región es requerida');
      return;
    }

    const submitData = {
      ...formData,
      founded: formData.founded ? parseInt(formData.founded) : null
    };

    if (editingWinery) {
      updateMutation.mutate({
        id: editingWinery.id,
        formData: submitData
      });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDeleteClick = (winery) => {
    setDeleteDialog({ open: true, winery });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.winery) {
      deleteMutation.mutate(deleteDialog.winery.id);
    }
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <Box>
      <BackButton />
      
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WineryIcon />
          Gestión de Bodegas
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Bodega
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar las bodegas
        </Alert>
      )}

      {/* Tabla de bodegas */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Bodega</TableCell>
              <TableCell>Región</TableCell>
              <TableCell>Fundada</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {wineries.map((winery) => (
              <TableRow key={winery.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{ 
                        bgcolor: 'secondary.main', 
                        width: 40, 
                        height: 40 
                      }}
                    >
                      <WineryIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {winery.name}
                      </Typography>
                      {winery.website && (
                        <Typography 
                          variant="caption" 
                          color="primary" 
                          component="a"
                          href={winery.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textDecoration: 'none' }}
                        >
                          {winery.website}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={winery.region}
                    color="primary"
                    variant="outlined"
                    size="small"
                    icon={<LocationIcon />}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {winery.founded}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {winery.description}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton
                      onClick={() => handleOpenDialog(winery)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton
                      onClick={() => handleDeleteClick(winery)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {wineries.length === 0 && !isLoading && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            No hay bodegas registradas
          </Typography>
        </Box>
      )}

      {/* Dialog para crear/editar bodega */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingWinery ? 'Editar Bodega' : 'Nueva Bodega'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                autoFocus
                label="Nombre de la Bodega"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />

              <TextField
                label="Región"
                fullWidth
                value={formData.region}
                onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                required
                placeholder="ej: Rioja, Ribera del Duero, Penedès"
              />

              <TextField
                label="Año de Fundación"
                type="number"
                fullWidth
                value={formData.founded}
                onChange={(e) => setFormData({ ...formData, founded: e.target.value })}
                inputProps={{ min: 1000, max: new Date().getFullYear() }}
              />

              <TextField
                label="Descripción"
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Breve descripción de la bodega"
              />

              <TextField
                label="Sitio Web"
                fullWidth
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://www.ejemplo.com"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="contained"
              color="secondary"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {editingWinery ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de confirmación para eliminar */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, winery: null })}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar la bodega "{deleteDialog.winery?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialog({ open: false, winery: null })}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminWineries; 